import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { StorageService } from '@coin/shared/core/data-access';
import { StorageKey } from '@coin/shared/core/util-enums';
import { ConfirmationDialogComponent } from '@coin/shared/core/ui-storybook';
import { ConfirmationDialogConfirm, ConfirmationDialogData } from '@coin/shared/core/util-models';
import { filter } from 'rxjs/operators';

/**
 * Controls the translation key viewer (debug mode that renders translation keys instead of values).
 * The flag is persisted in local storage and applied on the next page load, so toggling reloads the page.
 */
@Injectable({ providedIn: 'root' })
export class TranslationViewerService {
  private readonly dialog = inject(MatDialog);
  private readonly storageService = inject(StorageService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly isEnabled = signal(this.storageService.get(StorageKey.TRANSLATIONS_DEBUG_MODE_ENABLED) === 'true');

  public toggle(): void {
    const action = this.isEnabled() ? 'disable' : 'enable';

    this.dialog
      .open<ConfirmationDialogComponent, ConfirmationDialogData, ConfirmationDialogConfirm>(ConfirmationDialogComponent, {
        data: {
          headline: 'Please confirm',
          msg: `Do you want to ${action} the translation viewer? Clicking "Confirm" will reload the page.`,
          confirmMsg: 'Confirm',
          cancelMsg: 'Cancel'
        }
      })
      .afterClosed()
      .pipe(
        filter(result => !!result),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.storageService.set(StorageKey.TRANSLATIONS_DEBUG_MODE_ENABLED, action === 'enable' ? 'true' : 'false');
        window.location.reload();
      });
  }
}
